import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { fmtMoney } from "@/lib/format";

export default function CRMBoard() {
    const [pipelines, setPipelines] = useState([]);
    const [pipelineKey, setPipelineKey] = useState(null);
    const [opps, setOpps] = useState([]);
    const [dragOpp, setDragOpp] = useState(null);
    const [showNew, setShowNew] = useState(false);
    const [contacts, setContacts] = useState([]);

    const loadPipelines = async () => {
        const { data } = await api.get("/pipelines");
        setPipelines(data);
        if (data.length && !pipelineKey) setPipelineKey(data[0].key);
    };
    const loadOpps = async () => {
        if (!pipelineKey) return;
        const { data } = await api.get(`/opportunities?pipeline_key=${pipelineKey}&status=open`);
        setOpps(data);
    };
    const loadContacts = async () => {
        const { data } = await api.get("/contacts?limit=200");
        setContacts(data.items || []);
    };

    useEffect(() => { loadPipelines(); loadContacts(); }, []);
    useEffect(() => { loadOpps(); /* eslint-disable-next-line */ }, [pipelineKey]);

    const pipeline = pipelines.find((p) => p.key === pipelineKey);

    const columns = useMemo(() => {
        if (!pipeline) return [];
        const stages = [...(pipeline.stages || [])].sort((a, b) => a.order - b.order);
        return stages.map((s) => ({ ...s, opps: opps.filter((o) => o.stage_key === s.key) }));
    }, [pipeline, opps]);

    const drop = async (stage_key) => {
        if (!dragOpp) return;
        setOpps((os) => os.map((o) => (o.id === dragOpp.id ? { ...o, stage_key } : o)));
        try {
            await api.post(`/opportunities/${dragOpp.id}/stage`, { stage_key });
            toast.success("Stage updated");
        } catch {
            toast.error("Could not update");
            loadOpps();
        }
        setDragOpp(null);
    };

    const wonLost = async (opp, status) => {
        try {
            await api.post(`/opportunities/${opp.id}/wonlost`, { status });
            toast.success(`Marked ${status}`);
            loadOpps();
        } catch { toast.error("Failed"); }
    };

    return (
        <div className="p-6 md:p-10" data-testid="page-crm">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <div className="overline">CRM</div>
                    <h1 className="mt-2 font-display text-4xl tracking-tight leading-none">Pipeline</h1>
                </div>
                <div className="flex items-center gap-3">
                    <select value={pipelineKey || ""} onChange={(e) => setPipelineKey(e.target.value)} className="border border-border bg-background px-3 py-2 text-sm" data-testid="pipeline-select">
                        {pipelines.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
                    </select>
                    <button onClick={() => setShowNew(true)} className="bg-foreground text-background px-4 py-2 text-sm inline-flex items-center gap-2 hover:opacity-90" data-testid="new-opportunity-btn">
                        <Plus size={14} /> New Opportunity
                    </button>
                </div>
            </div>

            {opps.length === 0 && (
                <div className="border border-border p-8 mb-6 text-sm text-muted-foreground" data-testid="crm-empty-state">
                    Empty pipeline. Convert leads from your <Link to="/app/admin/leads" className="underline">inbox</Link>, or click "New Opportunity" above.
                </div>
            )}

            <div className="grid gap-4 overflow-x-auto pb-6" style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(240px, 1fr))` }} data-testid="crm-board">
                {columns.map((col) => (
                    <div
                        key={col.key}
                        className="border border-border bg-secondary/30 min-h-[400px] flex flex-col"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => drop(col.key)}
                        data-testid={`crm-col-${col.key}`}
                    >
                        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                            <div className="overline">{col.name}</div>
                            <div className="text-xs text-muted-foreground">{col.opps.length}</div>
                        </div>
                        <div className="p-3 space-y-2 flex-1">
                            {col.opps.map((o) => (
                                <div
                                    key={o.id}
                                    draggable
                                    onDragStart={() => setDragOpp(o)}
                                    className="p-4 border border-border bg-background cursor-move hover:border-foreground/60 transition-colors"
                                    data-testid={`opp-card-${o.id}`}
                                >
                                    <div className="text-sm font-medium">{o.name}</div>
                                    <div className="mt-1 text-xs text-muted-foreground">{fmtMoney(o.estimated_value)} · {o.probability_pct}%</div>
                                    <div className="mt-3 flex gap-2">
                                        <button onClick={() => wonLost(o, "won")} className="text-xs px-2 py-1 border border-border hover:bg-secondary" data-testid={`opp-won-${o.id}`}>Won</button>
                                        <button onClick={() => wonLost(o, "lost")} className="text-xs px-2 py-1 border border-border hover:bg-secondary" data-testid={`opp-lost-${o.id}`}>Lost</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <NewOpportunityDialog
                open={showNew}
                onOpenChange={setShowNew}
                pipelineKey={pipelineKey}
                pipeline={pipeline}
                contacts={contacts}
                onCreated={() => { setShowNew(false); loadOpps(); }}
            />
        </div>
    );
}

function NewOpportunityDialog({ open, onOpenChange, pipelineKey, pipeline, contacts, onCreated }) {
    const [form, setForm] = useState({ name: "", contact_id: "", estimated_value: 0, probability_pct: 50, notes: "" });
    useEffect(() => { if (open) setForm({ name: "", contact_id: "", estimated_value: 0, probability_pct: 50, notes: "" }); }, [open]);

    const submit = async (e) => {
        e.preventDefault();
        const stage_key = pipeline?.stages?.[0]?.key || "new";
        try {
            await api.post("/opportunities", {
                name: form.name,
                contact_id: form.contact_id || null,
                pipeline_key: pipelineKey,
                stage_key,
                estimated_value: Number(form.estimated_value) || 0,
                probability_pct: Number(form.probability_pct) || 50,
                status: "open",
                notes: form.notes,
            });
            toast.success("Opportunity created");
            onCreated();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="new-opportunity-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl">New opportunity</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <Label>Name</Label>
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-2" data-testid="opp-name" />
                    </div>
                    <div>
                        <Label>Contact (optional)</Label>
                        <select value={form.contact_id} onChange={(e) => setForm({ ...form, contact_id: e.target.value })} className="mt-2 w-full border border-border bg-background px-3 py-2 text-sm" data-testid="opp-contact">
                            <option value="">— none —</option>
                            {contacts.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} — {c.company || c.email || ""}</option>)}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label>Estimated value ($)</Label>
                            <Input type="number" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} className="mt-2" data-testid="opp-value" />
                        </div>
                        <div>
                            <Label>Probability (%)</Label>
                            <Input type="number" value={form.probability_pct} onChange={(e) => setForm({ ...form, probability_pct: e.target.value })} className="mt-2" data-testid="opp-probability" />
                        </div>
                    </div>
                    <div>
                        <Label>Notes</Label>
                        <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2" rows={3} />
                    </div>
                    <DialogFooter>
                        <button type="button" onClick={() => onOpenChange(false)} className="border border-border px-4 py-2 text-sm">Cancel</button>
                        <button type="submit" className="bg-foreground text-background px-4 py-2 text-sm hover:opacity-90" data-testid="opp-create-submit">Create</button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
